import { Component, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { SubSink } from 'subsink';
import { Store } from '@ngxs/store';
import { RegistrationService } from '../shared/registration.service';
import { RegistrationState, NotificationStateModel } from '../state/registration.state';
import { map, catchError } from 'rxjs/operators';
import { DownloadDocument } from 'src/app/shared/models/download-document.model';
import { ToastService } from 'src/app/shared/toast/toast.service';
import { of } from 'rxjs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

const ROUTE_PROFILE_DOCUMENT = 'profile/documents';
const LICENSE_DOCUMENT_TYPE = 'DriverLicense';
const PRCARD_DOCUMENT_TYPE = 'PRCard';
const TRAINING_CERTI_DOCUMENT = 'TrainingCertificate';
const PASSPORT_DOCUMENT_TYPE = 'Passport';
const CANADAIAN_VISA_DOCUMENT = 'CanadaVisa';
const US_VISA_DOCUMENT = 'USVisa';
const WORK_PERMIT_DOCUMENT = 'WorkPermit';
const DOCUMENT_TYPES = [
  LICENSE_DOCUMENT_TYPE,
  PRCARD_DOCUMENT_TYPE,
  TRAINING_CERTI_DOCUMENT,
  PASSPORT_DOCUMENT_TYPE,
  CANADAIAN_VISA_DOCUMENT,
  US_VISA_DOCUMENT,
  WORK_PERMIT_DOCUMENT
];
const ERROR_MESSAGE = 'Something Went Wrong.. please try again later';
const DELETE_MESSAGE = 'Are you sure you want to delete this document? This action cannot be undone.';
const DELETED_DOCUMENT = 'Document deleted successfully';
@Component({
    selector: 'app-additional-document-view',
    templateUrl: './additional-document-view.component.html',
    styleUrls: ['./additional-document-view.component.scss'],
    standalone: false
})
export class AdditionalDocumentViewComponent implements OnInit, OnDestroy {
  private subs = new SubSink();
  loading;
  selectedType: string;
  selectedId: string;
  fileName: string = '';
  showError: boolean = false;
  errorMessage: string = ERROR_MESSAGE;
  generatedDate: string = '';
  expiryDate: string = '';
  issueDate: any;
  fastCardNumber: any;
  healthCardNumber: any;
  credentialUrl: any;
  credentialId: any;
  issuingOrganization: any;
  previewImageUrl: string = null;
  previewPdfUrl: SafeResourceUrl = null;
  previewLoading: boolean = false;
  documentTypeDisplayMap: { [key: string]: string } = {
    EmergencyResponseTraining: 'Emergency Response Training',
    FatigueManagementTraining: 'Fatigue Management Training',
    HazardousMaterialsEndorsement: 'Hazardous Materials Endorsement',
    HoursOfService: 'Hours Of Service',
    MandatoryEntryLevelTraining: 'Mandatory Entry Level Training',
    MountainDriving: 'Mountain Driving',
    TransportationOfDangerousGoods: 'Transportation Of Dangerous Goods',
    VehicleInspections: 'Vehicle Inspections',
    WinterDriving: 'Winter Driving',
    WorkplaceHazardousMaterialsInformationSystem: 'Workplace Hazardous Materials Information System',
    DefensiveDriving: 'Defensive Driving',
    CargoSecurement: 'Cargo Securement',
    // Payroll document types
    VoidCheque: 'Void Cheque',
    SIN: 'SIN Document',
    ArticlesOfIncorporation: 'Articles of Incorporation',
    CertificateOfIncorporation: 'Certificate of Incorporation',
    HSTRegistration: 'HST Registration'
  };

  constructor(
    private readonly location: Location,
    private route: ActivatedRoute,
    private readonly store: Store,
    private readonly registrationService: RegistrationService,
    private readonly toastService: ToastService,
    private readonly sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => (this.selectedType = params.type));
    this.loadDriverDocuments();
  }
  backAction(): void {
    this.location.back();
  }
  ngOnDestroy() {
    this.subs.unsubscribe();
  }
  onDownloadFileClick(id: string) {
    this.registrationService.downloadDocument(id).subscribe(response => {
      this.openDocumentInNewTab(response);
    });
  }
  openDocumentInNewTab(document: DownloadDocument) {
    if (document && document.fileUrl) {
      window.open(document.fileUrl, '_blank');
    }
  }
  loadPreview(id: string): void {
    this.previewLoading = true;
    this.registrationService.downloadDocument(id).subscribe({
      next: (response: DownloadDocument) => {
        this.previewLoading = false;
        if (response && response.fileUrl) {
          const urlWithoutParams = response.fileUrl.split('?')[0];
          const extension = urlWithoutParams.split('/').pop()?.split('.').pop()?.toLowerCase();
          if (extension === 'pdf') {
            this.previewPdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(response.fileUrl + '#toolbar=0');
          } else {
            this.previewImageUrl = response.fileUrl;
          }
        }
      },
      error: () => {
        this.previewLoading = false;
      }
    });
  }
  loadDriverDocuments(): void {
    this.subs.add(
      this.registrationService
        .loadDriver()
        .pipe(map(() => this.store.selectSnapshot(RegistrationState.notification)))
        .subscribe((notificationStateModel: NotificationStateModel) => {
          this.loading = false;
          if (notificationStateModel && notificationStateModel.notifications) {
            const documents = notificationStateModel.notifications
              .filter(notification => notification.type === this.selectedType)
              .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());
            if (documents?.length) {
              this.selectedId = documents[0]?.id;
              this.loadPreview(this.selectedId);
              this.fileName = documents[0]?.typeDisplayName
                ? documents[0]?.typeDisplayName
                : documents[0]?.type
                ? this.documentTypeDisplayMap[documents[0]?.type]
                : documents[0]?.type;
              this.generatedDate = documents[0]?.generationDate;
              this.expiryDate = documents[0]?.validToDate;
              this.issueDate = documents[0]?.issueDate;
              this.fastCardNumber = documents[0]?.fastCardNumber;
              this.healthCardNumber = documents[0]?.healthCardNumber;
              this.issuingOrganization = documents[0]?.issuingOrganization;
              this.credentialId = documents[0]?.credentialId;
              this.credentialUrl = documents[0]?.credentialUrl;
            }

            if (this.fileName == '') {
              this.showError = true;
            }
          }
        })
    );
  }

  deleteDocument(id: string): void {
    if (confirm(DELETE_MESSAGE)) {
      this.registrationService
        .deleteDocument(id)
        .pipe(
          catchError(() => {
            this.toastService.showError(ERROR_MESSAGE);
            return of(false);
          })
        )
        .subscribe(() => {
          this.toastService.showSuccess(DELETED_DOCUMENT);
          this.backAction();
        });
    }
  }

  get showIssueDateFromApi(): boolean {
    if (this.issueDate == null || this.issueDate === '') {
      return false;
    }
    if (typeof this.issueDate === 'string' && !this.issueDate.trim()) {
      return false;
    }
    return true;
  }
}
